import { ConnectionType } from 'primary-schema';
import {
  type ConnectionSftpCreateBodyInput,
  type ConnectionSftpUpdateBody,
} from 'resource-gateway-schema';

import { type Common } from './common.js';
import {
  type Connection,
  type ConnectionApi,
  type ConnectionIntegrationEngine,
  type ConnectionInterfaceEngine,
  type ConnectionResource,
  type ConnectionSftp,
  type CreateSftpConnectionResponse,
  type FindAllOptions,
  type FindInterfaceEngineEventsOptions,
  type GetConnectionResourcesOptions,
  type GetFileSignedUrlOptions,
  type InterfaceEngineEventsResponse,
  type ResourceGatewaySftpConnectionRawResponse,
  SftpSourceGeneration,
  type SignedUrlResponse,
  type UpdateSftpConnectionResponse,
} from './models/connections/index.js';

export class API {
  common: Common;
  baseEndpoint = '/connections';

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(options: FindAllOptions): Promise<{ connections: Connection[] }> {
    const url = this.common.constructPath(this.baseEndpoint, options);
    return await this.common.getJSON(url);
  }

  async findOneById(args: {
    /**
     * FIXME: Remove detailPath as a parameter once we have one endpoint that handles all connection types.
     */
    detailPath: string;
  }): Promise<
    ConnectionApi | ConnectionSftp | ConnectionInterfaceEngine | ConnectionIntegrationEngine
  > {
    const { detailPath } = args;
    const url = this.common.constructPath(detailPath);
    const response = await this.common.getJSON(url);
    if ('connectionSettings' in response) {
      return {
        ...response,
        connectionType: ConnectionType.Sftp,
        sourceGeneration: SftpSourceGeneration.Channel,
      };
    }
    return response.connection;
  }

  async findEvents(
    options: FindInterfaceEngineEventsOptions
  ): Promise<InterfaceEngineEventsResponse> {
    const { detailPath, ...rest } = options;
    const url = this.common.constructPath(detailPath, rest);
    return await this.common.getJSON(url);
  }

  async createSftpConnection(
    values: ConnectionSftpCreateBodyInput
  ): Promise<CreateSftpConnectionResponse> {
    const url = this.common.constructPath('/resource-gateway/connections/sftp');
    const response = (await this.common.postJSON(
      url,
      values
    )) as ResourceGatewaySftpConnectionRawResponse;
    return {
      ...response,
      connectionType: ConnectionType.Sftp,
      sourceGeneration: SftpSourceGeneration.Channel,
    };
  }

  async updateSftpConnection(
    id: string,
    values: ConnectionSftpUpdateBody
  ): Promise<UpdateSftpConnectionResponse> {
    const url = this.common.constructPath(`/resource-gateway/connections/sftp/${id}`);
    const response = (await this.common.patchJSON(
      url,
      values
    )) as ResourceGatewaySftpConnectionRawResponse;
    return {
      ...response,
      connectionType: ConnectionType.Sftp,
      sourceGeneration: SftpSourceGeneration.Channel,
    };
  }

  /**
   * Get resource types for a connection (via resource-gateway proxy)
   */
  async getConnectionResourceTypes(args: {
    connectionId: string;
  }): Promise<{ resourceTypes: { resourceKey: string; name: string }[] }> {
    const { connectionId } = args;
    const url = this.common.constructPath(
      `/resource-gateway/connections/${connectionId}/resource-types`
    );
    return await this.common.getJSON(url);
  }

  /**
   * Get resources for a connection (via resource-gateway proxy)
   */
  async getConnectionResources(options: GetConnectionResourcesOptions): Promise<{
    resources: ConnectionResource[];
    limit: number;
    offset: number;
  }> {
    const { connectionId, ...query } = options;
    const url = this.common.constructPath(
      `/resource-gateway/connections/${connectionId}/resources`,
      query
    );
    return await this.common.getJSON(url);
  }

  /**
   * Get a signed URL for previewing/downloading a file (via resource-gateway proxy)
   */
  async getFileSignedUrl(options: GetFileSignedUrlOptions): Promise<SignedUrlResponse> {
    const { fileId, organizationId, disposition = 'inline' } = options;
    const url = this.common.constructPath(
      `/resource-gateway/resource-access/files/${fileId}/signed-url`,
      { organizationId, disposition }
    );
    return await this.common.getJSON(url);
  }
}
